import i18n from "@microdoing/shared/i18n";
import { getConversationId } from "@microdoing/shared/services/message/selectors";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";
import { getUserId } from "@microdoing/shared/services/user/selectors";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import theme from "app/Theme/Theme";
import { Box, MainText, PressableOpacity } from "app/components/common";
import MultipleAvatarSquare from "app/components/common/MultipleAvatarSquare";
import normalize from "app/utils/normalize";
import moment from "moment";
import React, { memo, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import localization from "moment/locale/fr";
import { StyleSheet } from "react-native";

interface IConversationRowProps {
  conversationData: ({ _id: string; name: any; picture: any } | undefined)[];
  conversationItemId: string;
  conversationIdGroup: string;
  conversationName: string;
  isNotSeen: boolean;
  createdAt: Date;
  user?: {
    _id: string;
    avatar: string;
    name: string;
  };
  text: string;
  openConversation: (
    conversationData,
    conversationId: string,
    conversationIdGroup: string,
  ) => void;
}

const SWIPEABLE_WIDTH = 125;

const ConversationRow = ({
  conversationData,
  conversationItemId,
  conversationIdGroup,
  conversationName,
  isNotSeen,
  createdAt,
  user = { _id: "", avatar: "", name: "" },
  text,
  openConversation,
}) => {
  const userId = useSelector(getUserId);
  const language = useSelector(getLanguage);
  const conversationId = useSelector(getConversationId);

  const displayLatestText = (user, text) => {
    if (user._id === userId) {
      return i18n.t("you-messaged", { text });
    }
    return text;
  };

  const handlePress = useCallback(() => {
    openConversation(conversationData, conversationItemId, conversationIdGroup);
  }, [conversationData, conversationItemId, conversationIdGroup]);

  const rowStyle = useMemo(
    () => [
      styles.userRow,
      {
        backgroundColor:
          conversationId === conversationItemId && isWebDesktop
            ? theme.colors["brand.100"]
            : theme.colors["neutral.white"],
      },
    ],
    [conversationId, conversationItemId],
  );

  return (
    <PressableOpacity onPress={handlePress} style={rowStyle}>
      <Box>
        <MultipleAvatarSquare users={conversationData} size={normalize(50)} />
      </Box>
      <Box
        marginLeft="m"
        justifyContent="space-between"
        flexDirection="row"
        flex={1}
      >
        <Box
          flex={1}
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <MainText variant={isNotSeen ? "l4-semibold" : "l4-medium"}>
            {conversationName}
          </MainText>
          {!!createdAt ? (
            <MainText
              variant={isNotSeen ? "l2-bold" : "l2-medium"}
              color={isNotSeen ? "neutral.900" : "neutral.400"}
              numberOfLines={1}
              textAlign="left"
            >
              {displayLatestText(user, text)}
            </MainText>
          ) : (
            <MainText
              variant="l2-medium"
              color="neutral.700"
              numberOfLines={1}
              textAlign="left"
            >
              {i18n.t("communicate-conversation-team")}
            </MainText>
          )}
        </Box>
        {isNotSeen && (
          <Box
            alignSelf="center"
            marginRight="s"
            width={normalize(10)}
            height={normalize(10)}
            backgroundColor="system.red.900"
            borderRadius={normalize(10) / 2}
          />
        )}
        {!!createdAt && (
          <Box justifyContent="center">
            <MainText variant="l2-medium" color="neutral.400">
              {language === "fr"
                ? moment(createdAt).locale("fr", localization).format("dddd")
                : moment(createdAt).locale("en").format("dddd")}
            </MainText>
          </Box>
        )}
      </Box>
    </PressableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    height: 80,
    borderBottomColor: "#ccc",
    width: "100%",
  },

  rightContent: {
    backgroundColor: "red",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    top: 0,
    bottom: 0,
    right: 0,
    width: SWIPEABLE_WIDTH,
  },
  deleteButton: {
    color: "#fff",
  },
  userRow: {
    alignSelf: "center",
    paddingVertical: theme.spacing.xs,
    paddingHorizontal: theme.spacing.m,
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
});

export default memo(ConversationRow);
